import { useEffect, useRef } from 'react';

import { useIsMotionReduced } from './useIsMotionReduced.js';

/** How long after the last touch the page waits before it starts sliding back. */
const SETTLE_MS = 5000;
const DRIFT_PX_PER_SECOND = 45;
const MS_PER_SECOND = 1000;

/**
 * The window is only one of the things that scroll, and not the only axis: the
 * coming-up strip runs left to right on a desktop, so a room is just as likely
 * to be left looking at the middle of it as at the bottom of the page.
 */
const scrolledElements = (): readonly Element[] =>
  [...document.querySelectorAll('*')].filter(
    (element) => element.scrollTop > 0 || element.scrollLeft > 0,
  );

const slideWindowBack = (drift: number): void => {
  if (scrollY > 0 || scrollX > 0) {
    scrollTo({
      top: Math.max(0, scrollY - drift),
      left: Math.max(0, scrollX - drift),
      behavior: 'auto',
    });
  }
};

const slideElementsBack = (elements: readonly Element[], drift: number): readonly Element[] =>
  elements
    .map((element) => {
      element.scrollTo({
        top: Math.max(0, element.scrollTop - drift),
        left: Math.max(0, element.scrollLeft - drift),
        behavior: 'auto',
      });
      return element;
    })
    .filter((element) => element.scrollTop > 0 || element.scrollLeft > 0);

/**
 * A projector view somebody scrolled through and walked away from slides back
 * to the start of its own accord — both axes, since the coming-up strip scrolls
 * sideways — five seconds after they stop, slowly enough to read as the screen
 * settling rather than as something grabbing the page.
 * Any wheel, touch, key or click stops it and starts the wait again.
 *
 * The slide is done frame by frame rather than through smooth scrolling, so a
 * person can take the page back mid-slide simply by scrolling. Nothing listens
 * for `scroll` itself, which would hear this drift as use.
 *
 * Finding the inner scrollers means walking the document, which is far too
 * expensive to repeat per frame, so the result is held until either the room
 * touches the page or the DOM changes under it.
 */
export const useIdleReturnToTop = (): void => {
  const isMotionReduced = useIsMotionReduced();
  const draftLastActivityAt = useRef(0);
  const draftFrame = useRef(0);
  const draftLastFrameAt = useRef(0);
  const draftScrollers = useRef<readonly Element[] | undefined>(undefined);

  useEffect(() => {
    draftLastActivityAt.current = Date.now();
    draftLastFrameAt.current = performance.now();
    draftScrollers.current = undefined;

    const controller = new AbortController();
    const options = { passive: true, signal: controller.signal };
    const noteActivity = (): void => {
      draftLastActivityAt.current = Date.now();
      draftScrollers.current = undefined;
    };

    addEventListener('wheel', noteActivity, options);
    addEventListener('touchmove', noteActivity, options);
    addEventListener('touchstart', noteActivity, options);
    addEventListener('pointerdown', noteActivity, options);
    addEventListener('keydown', noteActivity, options);

    const observer = new MutationObserver(() => {
      draftScrollers.current = undefined;
    });
    observer.observe(document.body, { childList: true, subtree: true });

    const step = (frameAt: number): void => {
      const elapsedMs = frameAt - draftLastFrameAt.current;
      draftLastFrameAt.current = frameAt;
      draftFrame.current = requestAnimationFrame(step);

      if (Date.now() - draftLastActivityAt.current < SETTLE_MS) {
        return;
      }

      draftScrollers.current ??= scrolledElements();

      const drift = isMotionReduced ? Infinity : (DRIFT_PX_PER_SECOND * elapsedMs) / MS_PER_SECOND;

      slideWindowBack(drift);
      draftScrollers.current = slideElementsBack(draftScrollers.current, drift);
    };

    draftFrame.current = requestAnimationFrame(step);

    return (): void => {
      cancelAnimationFrame(draftFrame.current);
      observer.disconnect();
      controller.abort();
    };
  }, [isMotionReduced]);
};
